import React, { useState } from 'react';
import { whatsappapi } from '../api/api';
import Swal from 'sweetalert2';

export default function IntegrationGuideModal({ app, onClose }) {
  const [activeTab, setActiveTab] = useState('python');
  const baseUrl = `${whatsappapi}/api/v1/send`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire("Code snippet copied to clipboard!");
  };

  const languages = [
    { id: 'python', name: 'Python', icon: 'fab fa-python', color: '#3776ab' },
    { id: 'node', name: 'Node.js', icon: 'fab fa-node-js', color: '#339933' },
    { id: 'php', name: 'PHP', icon: 'fab fa-php', color: '#777bb4' },
    { id: 'java', name: 'Java', icon: 'fab fa-java', color: '#007396' },
    { id: 'csharp', name: '.NET', icon: 'fas fa-hashtag', color: '#239120' },
    { id: 'curl', name: 'cURL', icon: 'fas fa-terminal', color: '#4d4d4d' }
  ];

  // Helper to get code based on active tab
  const getCodeSnippet = () => {
    switch (activeTab) {
      case 'python': return pythonCode(baseUrl, app.api_key);
      case 'node': return nodeCode(baseUrl, app.api_key);
      case 'php': return phpCode(baseUrl, app.api_key);
      case 'java': return javaCode(baseUrl, app.api_key);
      case 'csharp': return netCode(baseUrl, app.api_key);
      case 'curl': return curlCode(baseUrl, app.api_key);
      default: return "";
    }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.85)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-dark text-white border-0">
            <h5 className="modal-title fw-bold">
              <i className="fa fa-terminal me-2 text-info"></i>
              API Integration Guide: {app.name}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0">
            {/* Tab Navigation */}
            <div className="bg-light border-bottom d-flex overflow-auto">
              {languages.map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => setActiveTab(lang.id)}
                  className={`btn rounded-0 px-4 py-3 border-0 d-flex align-items-center gap-2 transition-all ${
                    activeTab === lang.id 
                    ? 'bg-white border-bottom border-primary text-primary fw-bold' 
                    : 'text-muted'
                  }`}
                  style={activeTab === lang.id ? { borderBottomWidth: '3px !important' } : {}}
                >
                  <i className={`${lang.icon}`} style={{ color: activeTab === lang.id ? lang.color : 'inherit' }}></i>
                  {lang.name}
                </button>
              ))}
            </div>

            <div className="p-4 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="small text-muted">Endpoint: <code className="text-primary">{baseUrl}</code></span>
                <button className="btn btn-sm btn-outline-primary" onClick={() => copyToClipboard(getCodeSnippet())}>
                  <i className="fa fa-copy me-1"></i> Copy Code
                </button>
              </div>

              {/* Code Display Area */}
              <div className="position-relative">
                <pre className="bg-dark text-light p-3 rounded shadow-sm overflow-auto" style={{ maxHeight: '400px', fontSize: '13px', lineHeight: '1.6' }}>
                  <code className="font-monospace">{getCodeSnippet()}</code>
                </pre>
              </div>

              <div className="mt-4">
                <h6 className="fw-bold small text-uppercase text-muted mb-2">Required Headers</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered small">
                    <thead className="bg-light">
                      <tr>
                        <th>Header</th>
                        <th>Value</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>x-api-key</code></td>
                        <td><code>{app.api_key}</code></td>
                        <td>Your secret authentication key.</td>
                      </tr>
                      <tr>
                        <td><code>Content-Type</code></td>
                        <td><code>application/json</code></td>
                        <td>Format of the request body.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer border-0">
            <button type="button" className="btn btn-secondary px-4" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Code Snippets (Same as previous but added PHP and cURL) ---

const pythonCode = (url, key) => `import requests

url = "${url}"
headers = {"x-api-key": "${key}"}
payload = {
    "to": "919876543210",
    "message": "Hello from Python!",
    "session_id": "session_id",
    "device_id": "device_id"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`;

const nodeCode = (url, key) => `const axios = require('axios');

axios.post('${url}', {
    to: '919876543210',
    message: 'Hello from Node.js',
    session_id: 'session_id',
    device_id: 'device_id'
}, {
    headers: { 'x-api-key': '${key}' }
})
.then(res => console.log(res.data))
.catch(err => console.error(err));`;

const phpCode = (url, key) => `<?php
$curl = curl_init();

curl_setopt_array($curl, [
  CURLOPT_URL => "${url}",
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => json_encode([
    "to" => "919876543210",
    "message" => "Hello from PHP",
    "session_id" => "session_id",
    "device_id" => "device_id"
  ]),
  CURLOPT_HTTPHEADER => [
    "Content-Type: application/json",
    "x-api-key: ${key}"
  ],
]);

$response = curl_exec($curl);
curl_close($curl);
echo $response;`;

const javaCode = (url, key) => `// Using OkHttp Library
OkHttpClient client = new OkHttpClient();

MediaType mediaType = MediaType.parse("application/json");
RequestBody body = RequestBody.create(mediaType, 
    "{\\"to\\":\\"919876543210\\",\\"message\\":\\"Hello from Java\\",\\"session_id\\":\\"session_id\\",\\"device_id\\":\\"1\\"}");

Request request = new Request.Builder()
  .url("${url}")
  .post(body)
  .addHeader("x-api-key", "${key}")
  .build();

Response response = client.newCall(request).execute();`;

const netCode = (url, key) => `var client = new HttpClient();
client.DefaultRequestHeaders.Add("x-api-key", "${key}");

var json = "{\\"to\\":\\"919876543210\\",\\"message\\":\\"Hello from .NET\\",\\"session_id\\":\\"session_id\\",\\"device_id\\":\\"1\\"}";
var content = new StringContent(json, Encoding.UTF8, "application/json");

var response = await client.PostAsync("${url}", content);
string result = await response.Content.ReadAsStringAsync();`;

const curlCode = (url, key) => `curl -X POST ${url} \\
  -H "x-api-key: ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "919876543210",
    "message": "Hello via cURL",
    "session_id": "session_id",
    "device_id": "device_id"
  }'`;